/**
 * Parse custom:groups claim from any authorizer format.
 * Accepts the formats backend _parse_group_claim in escalation.py accepts.
 *
 * Supported formats:
 * - JSON array: '["msreportingadmin","ticket-handler"]'
 * - Bracketed comma-separated: '[msreportingadmin, ticket-handler]'
 * - Bracketed space-separated: '[msreportingadmin ticket-handler]'
 * - Plain CSV: 'msreportingadmin,ticket-handler'
 */
export function parseCustomGroups(raw: string | undefined | null): string[] {
  if (!raw || !raw.trim()) return [];
  const trimmed = raw.trim();

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((g: string) => g.trim()).filter(Boolean);
      }
    } catch {
      // not valid JSON, fall through to bracket stripping
    }
    const inner = trimmed.endsWith(']')
      ? trimmed.slice(1, -1).trim()
      : trimmed.slice(1).trim();
    if (inner.includes(',')) {
      return inner.split(',').map(g => g.trim()).filter(Boolean);
    }
    return inner.split(/\s+/).filter(Boolean);
  }

  return trimmed.split(',').map(g => g.trim()).filter(Boolean);
}

/**
 * Merge cognito:groups (array) and custom:groups (string) into a single group list,
 * deduplicated preserving first occurrence. For display only: authorization reads the level
 * groups and opspilot-admin from cognito:groups, and only msreportingadmin from custom:groups
 * (backend authorization_groups in escalation.py).
 */
export function parseGroupsFromClaims(
  cognitoGroups: string[] | undefined,
  customGroupsRaw: string | undefined,
): string[] {
  const groups: string[] = [];
  if (cognitoGroups) {
    groups.push(...cognitoGroups.filter(g => g && g.trim()));
  }
  groups.push(...parseCustomGroups(customGroupsRaw));
  return [...new Set(groups)];
}
